/**
 * Utility to parse and render markdown to React elements
 */
import React from 'react';
import { Link } from 'react-router-dom';

export const parseMarkdownToReact = (text, options = {}) => {
  if (!text) return null;
  
  const {
    renderMentions = true,
    renderLinks = true,
    renderBold = true,
    renderItalic = true,
    renderStrike = true,
    renderLineBreaks = true,
    mentionedUsers = []
  } = options;

  // Process markdown in the correct order to avoid conflicts
  const elements = [];
  let keyIndex = 0;
  
  // Split by lines first to handle line breaks
  const lines = text.split('\n');
  
  lines.forEach((line, lineIndex) => {
    if (line.trim() || lineIndex === 0) {
      const lineElements = processLine(line, {
        renderMentions,
        renderLinks,
        renderBold,
        renderItalic,
        renderStrike,
        mentionedUsers,
        keyIndex
      });
      
      elements.push(...lineElements);
      keyIndex += lineElements.length;
    }
    
    // Add line break if not last line
    if (lineIndex < lines.length - 1) {
      elements.push(<br key={`br-${keyIndex++}`} />);
    }
  });
  
  return elements.length > 0 ? elements : null;
};

function processLine(text, options) {
  const { keyIndex: startKey } = options;
  let keyIndex = startKey || 0;
  const segments = [];
  
  // Create a combined pattern for all markdown elements
  const patterns = [];
  
  if (options.renderLinks) {
    patterns.push({
      name: 'link',
      regex: /\[([^\]]+)\]\(([^)]+)\)/g,
      render: (match, text, url) => (
        <a 
          href={url} 
          target="_blank" 
          rel="noopener noreferrer" 
          className="text-blue-600 underline hover:text-blue-800"
        >
          {text}
        </a>
      )
    });
  }
  
  if (options.renderBold) {
    patterns.push({
      name: 'bold',
      regex: /\*\*([^*]+)\*\*/g,
      render: (match, text) => <strong>{text}</strong>
    });
  }
  
  if (options.renderItalic) {
    patterns.push({
      name: 'italic',
      regex: /\*([^*]+)\*/g,
      render: (match, text) => <em>{text}</em>
    });
  }
  
  if (options.renderStrike) {
    patterns.push({
      name: 'strike',
      regex: /~~([^~]+)~~/g,
      render: (match, text) => <del>{text}</del>
    });
  }
  
  if (options.renderMentions) {
    patterns.push({
      name: 'mention',
      regex: /@(\w+)/g,
      render: (match, username) => {
        // Render mentions as React Router links to user profile
        return (
          <Link 
            to={`/profile/${username}`}
            className="text-blue-600 font-medium cursor-pointer hover:underline"
          >
            @{username}
          </Link>
        );
      }
    });
  }
  
  // Process the text
  let remaining = text;
  const allMatches = [];
  
  // Find all matches
  patterns.forEach(pattern => {
    let match;
    pattern.regex.lastIndex = 0; // Reset regex
    while ((match = pattern.regex.exec(text)) !== null) {
      allMatches.push({
        start: match.index,
        end: match.index + match[0].length,
        pattern: pattern,
        match: match
      });
    }
  });
  
  // Sort matches by position
  allMatches.sort((a, b) => a.start - b.start);
  
  // Process matches, avoiding overlaps
  let lastEnd = 0;
  const processedMatches = [];
  
  for (const matchInfo of allMatches) {
    if (matchInfo.start >= lastEnd) {
      processedMatches.push(matchInfo);
      lastEnd = matchInfo.end;
    }
  }
  
  // Build the result
  lastEnd = 0;
  processedMatches.forEach(matchInfo => {
    // Add text before the match
    if (matchInfo.start > lastEnd) {
      segments.push(
        <span key={`text-${keyIndex++}`}>
          {text.substring(lastEnd, matchInfo.start)}
        </span>
      );
    }
    
    // Add the matched element
    const rendered = matchInfo.pattern.render(...matchInfo.match);
    segments.push(
      React.cloneElement(rendered, { key: `${matchInfo.pattern.name}-${keyIndex++}` })
    );
    
    lastEnd = matchInfo.end;
  });
  
  // Add remaining text
  if (lastEnd < text.length) {
    segments.push(
      <span key={`text-${keyIndex++}`}>
        {text.substring(lastEnd)}
      </span>
    );
  }
  
  return segments.length > 0 ? segments : [<span key={`text-${keyIndex}`}>{text}</span>];
}